import {reportIpmAction, type IpmMetricTags} from '@github-ui/ipm-telemetry'
import {InfoIcon} from '@primer/octicons-react'
import {Button, Link} from '@primer/react'
import {useMemo} from 'react'
import type {
  BannerAnalytics,
  BannerSegment,
  TransactionalMessageBanner as TransactionalMessageBannerType,
} from '@github-ui/code-view-types'
import {IpmBannerBase} from '../ipm-banner-base/IpmBannerBase'

export type TransactionalMessageBannerDisplayStyle = 'full-bleed' | 'inset'

export interface TransactionalMessageBannerProps {
  banner: TransactionalMessageBannerType
  displayStyle: TransactionalMessageBannerDisplayStyle
}

function getVisibleAnalyticsEvent(analytics: BannerAnalytics) {
  return JSON.stringify({
    category: analytics.category,
    action: 'visible',
    label: `location:${analytics.location}`,
  })
}

function getDismissAnalyticsEvent(analytics: BannerAnalytics) {
  return JSON.stringify({
    category: analytics.category,
    action: 'dismiss',
    label: `location:${analytics.location}`,
  })
}

function getClickAnalyticsEvent(analytics: BannerAnalytics) {
  return JSON.stringify({
    category: analytics.category,
    action: 'click',
    label: `location:${analytics.location}`,
  })
}

// Generate a stable key for each segment based on its content
function getSegmentKey(segment: BannerSegment, index: number): string {
  return `${segment.type}-${index}-${segment.content.slice(0, 20)}`
}

export function TransactionalMessageBanner({banner, displayStyle}: TransactionalMessageBannerProps) {
  const isRounded = displayStyle === 'inset'
  const hasCta = Boolean(banner.cta)
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'TransactionalMessageBanner',
      surface: 'banner',
      display_location: banner.analytics.location,
      category: banner.analytics.category,
      nudge_id: 'transactional_message_banner',
      variant: hasCta ? 'with_cta' : 'message',
    }),
    [banner.analytics.category, banner.analytics.location, hasCta],
  )

  return (
    <IpmBannerBase
      dismissPath={banner.dismissPath}
      visibleEvent={getVisibleAnalyticsEvent(banner.analytics)}
      dismissEvent={getDismissAnalyticsEvent(banner.analytics)}
      testId="transactional-message-banner"
      title="Important update"
      icon={<InfoIcon />}
      location={banner.analytics.location}
      rounded={isRounded}
      telemetryTags={telemetryTags}
      description={
        <span className="text-normal">
          {banner.segments.map((segment, index) =>
            segment.type === 'link' ? (
              <Link
                key={getSegmentKey(segment, index)}
                inline
                href={segment.href}
                className="Link--inTextBlock"
                onClick={() => reportIpmAction('click', {...telemetryTags, variant: 'body_link'})}
              >
                {segment.content}
              </Link>
            ) : (
              <span key={getSegmentKey(segment, index)}>{segment.content}</span>
            ),
          )}
        </span>
      }
      primaryAction={
        banner.cta ? (
          <Button
            as="a"
            href={banner.cta.href}
            // Smaller button for full-bleed strips (square corners under page chrome),
            // larger button for inset banners (card-style within page content) where
            // the heavier visual weight reads as a deliberate primary action.
            size={isRounded ? 'medium' : 'small'}
            data-analytics-event={getClickAnalyticsEvent(banner.analytics)}
            onClick={() => reportIpmAction('cta_click', telemetryTags)}
          >
            {banner.cta.label}
          </Button>
        ) : undefined
      }
    />
  )
}
